export type TrafficLightRating = "RED" | "AMBER" | "GREEN";
export type PromptDataType = "categorical" | "numeric" | "date" | "currency" | "relevancy";
export type PromptAutoRefreshFrequency = "daily" | "weekly" | "monthly";

export type TrafficLightBadgeVariant = "traffic-red" | "traffic-amber" | "traffic-green";

export const TRAFFIC_LIGHT_VARIANT: Record<TrafficLightRating, TrafficLightBadgeVariant> = {
  RED: "traffic-red",
  AMBER: "traffic-amber",
  GREEN: "traffic-green",
};

export const TRAFFIC_LIGHT_OPTIONS: Array<{ label: TrafficLightRating; value: TrafficLightRating }> = [
  { label: "RED", value: "RED" },
  { label: "AMBER", value: "AMBER" },
  { label: "GREEN", value: "GREEN" },
];

export type SnapshotReviewVerdict = "material_difference" | "no_material_difference" | "inconclusive";
export type SnapshotReviewRunStatus = "IN_PROGRESS" | "COMPLETED" | "FAILED" | "CANCELLED";

export interface SnapshotReviewVerdictCounts {
  material_difference: number;
  no_material_difference: number;
  inconclusive: number;
}

export interface SnapshotCellReview {
  verdict: SnapshotReviewVerdict | null;
  short_explanation: string | null;
  status: string;
}

export interface SnapshotCellReviewDetails extends SnapshotCellReview {
  detailed_explanation: string | null;
  second_response: string | null;
  error_message: string | null;
}

export type ReviewVerdictBadgeVariant = "review-red" | "review-amber" | "review-neutral";

export const REVIEW_VERDICT_VARIANT: Record<SnapshotReviewVerdict, ReviewVerdictBadgeVariant> = {
  material_difference: "review-red",
  inconclusive: "review-amber",
  no_material_difference: "review-neutral",
};

export const REVIEW_VERDICT_LABEL: Record<SnapshotReviewVerdict, string> = {
  material_difference: "Material difference",
  no_material_difference: "No material difference",
  inconclusive: "Inconclusive",
};

export const REVIEW_VERDICT_OPTIONS: Array<{ label: string; value: SnapshotReviewVerdict }> = [
  { label: "Material difference", value: "material_difference" },
  { label: "Inconclusive", value: "inconclusive" },
  { label: "No material difference", value: "no_material_difference" },
];

/** "blank" shows "-" in the summaries collection; "copy_master" copies the master document's value. */
export type FamilySummaryMode = "summarise" | "copy_master" | "blank";

export interface BusinessReportPrompt {
  id: string;
  name: string;
  prompt: string;
  created_at: string;
  updated_at: string;
  order: number;
  full_context: boolean;
  is_date: boolean;
  data_type: PromptDataType | null;
  use_image: boolean;
  no_retriever: boolean;
  web_search: boolean;
  traffic_light: boolean;
  auto_refresh_frequency: PromptAutoRefreshFrequency | null;
  /** How this column is filled in a contract family summary row. */
  family_summary_mode?: FamilySummaryMode;
  /** The consolidation prompt; only used when `family_summary_mode` is "summarise". */
  family_summary_prompt?: string | null;
  /** Sent as context with every other column's family summary. */
  family_metadata?: boolean;
}

export interface TemplateCategory {
  id: string;
  name: string;
  order: number;
  owner_id: string;
  created_at: string;
  updated_at: string;
}

export interface TemplateFavorite {
  id: string;
  business_report_id: string;
  created_at: string;
}

export interface TemplateGroup {
  label: string;
  categoryId: string | null;
  templates: BusinessReport[];
}

export type BusinessReportType = "snapshot_template" | "business_report" | "authoring_template";

/** `organization`: every member of the tenant sees it; `private`: only its owner and library admins. */
export type TemplateVisibility = "private" | "organization";

export const isPrivateTemplate = (template?: { visibility?: TemplateVisibility } | null): boolean =>
  template?.visibility === "private";

export const isBusinessReportType = (type?: BusinessReportType | null): boolean => type === "business_report";

export const isBusinessReportTemplate = (template?: BusinessReportSummary | null): boolean =>
  isBusinessReportType(template?.type);

/** Document-authoring checks: prompts run as tracked changes over a .docx, never as a snapshot. */
export const isAuthoringTemplateType = (type?: BusinessReportType | null): boolean => type === "authoring_template";

export const isAuthoringTemplate = (template?: BusinessReportSummary | null): boolean =>
  isAuthoringTemplateType(template?.type);

/** Only SnapShot templates can start a snapshot collection. */
export const isSnapshotTemplate = (template?: BusinessReportSummary | null): boolean =>
  template?.type === "snapshot_template";

export const isBusinessReportOutcome = (outcome?: BusinessReportOutcome | null): boolean =>
  isBusinessReportTemplate(outcome?.business_report);

// Any published contract family row (whole family or single document). The API rejects
// regeneration on them; anyone who can see one may verify it, and only cells of the active
// version can be edited.
export const isFamilySummaryOutcome = (outcome?: BusinessReportOutcome | null): boolean => !!outcome?.summary_row_kind;

// A superseded family version is history: the API rejects edits to it.
export const canEditOutcomeResults = (outcome?: BusinessReportOutcome | null): boolean =>
  outcome?.family_version_status !== "superseded";

// A family summary's source snapshot as the family-sources endpoint returns it.
export type FamilySourceOutcome = Pick<
  BusinessReportOutcomeDetails,
  "id" | "name" | "fs_entry" | "result" | "evidences"
>;

export interface BusinessReport {
  is_active: boolean;
  id: string;
  name: string;
  created_at: string;
  last_used_at: string | null;
  updated_at: string;
  prompts: BusinessReportPrompt[];
  owner_id: string;
  visibility: TemplateVisibility;
  description: string | null;
  variables: string[];
  category_id: string | null;
  category: Pick<TemplateCategory, "id" | "name"> | null;
  type: BusinessReportType;
}

export interface PlacedSnapshotCitation {
  snapshot_outcome_result_id: string;
  snapshot_outcome_id: string;
  start_offset: number;
  end_offset: number;
}

export interface BusinessReportOutcomeResult {
  id: string;
  result: string;
  business_report_prompt_id: string | null;
  name: string;
  prompt: string | null;
  order: number;
  date: string | null;
  is_date: boolean;
  data_type: PromptDataType | null;
  is_note: boolean;
  error_message: string | null;
  edited_by_id: string | null;
  edited_at: string | null;
  traffic_light: boolean;
  rating: TrafficLightRating | null;
  currency_amount?: string | null;
  currency_code?: string | null;
  relevancy?: boolean | null;
  skip_reason?: "relevancy_false" | null;
  normalized_is_na?: boolean;
  review?: SnapshotCellReview | null;
}

/**
 * Structured companion of a result's plain `result` text. `kind` and `version` pick the renderer
 * (see resultDetails/ResultDetailsSection); `data` is that kind's payload.
 */
export interface ResultDetails {
  kind: string;
  version: number;
  data: Record<string, unknown>;
}

export interface BusinessReportOutcomeDetailResult extends BusinessReportOutcomeResult {
  full_context: boolean;
  use_image: boolean;
  no_retriever: boolean;
  web_search: boolean;
  web_search_sources: import("@/types/open-chat").SearchSourcesData;
  snapshot_citations: PlacedSnapshotCitation[];
  result_details?: ResultDetails | null;
  review?: SnapshotCellReviewDetails | null;
}

export interface BusinessReportOutcomeEvidenceResult {
  fse_id: string;
  document_id: string;
  highlights: string;
  citation_id: string;
  answer_start_offset: number;
  answer_end_offset: number;
  spreadsheet_target?: {
    sheet_name: string;
    cell_ref: string;
    source_value?: string;
    match_kind?: "model_cell_ref" | "normalized_value" | "exact_value";
  };
}

export interface BusinessReportOutcomeEvidence {
  result: BusinessReportOutcomeEvidenceResult[];
  id: string;
  business_report_prompt_id: string;
}

export type BusinessReportOutcomeStatus = "FAILED" | "COMPLETED" | "IN_PROGRESS" | "NEW" | "CANCELLED";

export interface BusinessReportOutcomeFsEntry {
  content_type?: string | null;
  document_id: string | null;
  document_name: string;
  folder_id: string | null;
  id: string;
  parent_id: string | null;
  type: "folder" | "file";
}

export type BusinessReportSummary = Omit<BusinessReport, "prompts" | "variables">;

export interface BusinessReportOutcome {
  has_snapshot_events: boolean;
  answer: string;
  access: BusinessReportOutcomePermissionAccess;
  verified: boolean;
  permissions: BusinessReportOutcomePermission[];
  snapshot_progress: number;
  name: string;
  created_at: string;
  id: string;
  status: BusinessReportOutcomeStatus;
  error_message: string | null;
  business_report_id: string;
  business_report: BusinessReportSummary;
  fs_entry: BusinessReportOutcomeFsEntry | null;
  owner_id: string;
  snapshot_tx_id: string;
  snapshot_collection_id: string | null;
  result: BusinessReportOutcomeResult[];
  summary_row_kind?: "contract_summary" | "document_summary" | null;
  supplier_name?: string | null;
  contract_family_owner_user_id?: string | null;
  logical_family_id?: string | null;
  /** Family rows only: the active version is the editable one. */
  family_version_status?: "active" | "superseded" | null;
  /** Family rows only: when the folder the family was anchored at was deleted; the row is frozen. */
  folder_deleted_at?: string | null;
  /** Family rows only: when the folder lost its last document; the row is frozen. */
  folder_emptied_at?: string | null;
}

export interface BusinessReportOutcomeDetails extends BusinessReportOutcome {
  created_at: string;
  updated_at: string;
  business_report: BusinessReport;
  result: BusinessReportOutcomeDetailResult[];
  evidences: BusinessReportOutcomeEvidence[];
}

export interface VariablesDict {
  [key: string]: string;
}
export type BusinessReportOutcomePermissionRole = "owner" | "viewer";

export interface BusinessReportOutcomePermission {
  business_report_outcome_id: string;
  id: string;
  role: BusinessReportOutcomePermissionRole;
  user_id: string;
}
export type BusinessReportOutcomePermissionAccess = "public" | "restricted";

export interface GetSnapshotEventResponseItem {
  business_report_outcome_id?: string | null;
  business_report_outcome_result_id?: string | null;
  created_at: string;
  document_name: string;
  email_message: string;
  email_subject: string;
  event_date: string;
  event_type: string;
  fse_id: string;
  id: string;
  include_document_in_attachment: boolean;
  reminder_interval: number;
  reminder_frequency: string;
  reminder_end_after_occurrences: number;
  reminder_week_days?: Array<string>;
  required_only_one_done: boolean;
  reminder_start_date: string | null;
  reminder_end_date: string | null;
  status: "in_progress" | "done";
  timezone: string;
  title: string;
  recipients: Array<{
    id: string;
    email: string;
    status: string;
    completion_date: string;
    is_readonly: boolean;
  }>;
}
export interface GetSnapshotEventGroupedResponseItem {
  id: string;
  action_takers: string[];
  collection: string;
  creator: string;
  title: string;
  event_type: string;
  email_subject: string;
  fse_id: string;
  email_message: string;
  include_document_in_attachment: boolean;
  business_report_outcome_result_id: string;
  business_report_outcome_name: string;
  business_report_outcome_id: string;
  document_name: string;
  event_date: string;
  status: "overdue" | "in_progress" | "done";
  is_overdue: boolean;
  reminder_interval: number;
  reminder_frequency: string;
  reminder_end_after_occurrences: number;
  reminder_week_days?: Array<string>;
  required_only_one_done: boolean;
  reminder_start_date: string | null;
  reminder_end_date: string | null;
  recipients: Array<{
    id: string;
    email: string;
    status: string;
    completion_date: string;
    is_readonly: boolean;
  }>;
}

export interface GetSnapshotEventGroupedFiltersResponse {
  recipients: Array<string>;
  collections: Array<{ id: string; name: string }>;
  prompts: Array<{ id: string; name: string }>;
  creator_ids: Array<string>;
}

export type ColumnAnalysisStatus = "pending" | "completed";

export interface ColumnAnalysisResponse {
  id: string;
  business_report_id: string;
  snapshot_collection_id: string;
  subcollection_id: string | null;
  field_name: string;
  prompt: string;
  result: string | null;
  status: ColumnAnalysisStatus;
  contract_family_projection: boolean;
  created_at: string;
  updated_at: string;
  owner_id: string;
}

export const WITHOUT_EVENT_FILTER_KEY = "collection";

export interface SnapshotEventMail {
  id: string;
  title: string;
  message: string;
  owner_id: string;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateSnapshotEventMailPayload {
  title: string;
  message: string;
  is_default: boolean;
}

export interface UpdateSnapshotEventMailPayload {
  title?: string;
  message?: string;
  is_default?: boolean;
}

export type SnapshotEventReminderFrequency = "DAILY" | "WEEKLY" | "MONTHLY" | "YEARLY";
